import type { TicketPriority, TicketStatus } from "./api";

/**
 * Status and priority presentation.
 *
 * Every entry pairs a colour token with a text label. Priority and SLA state
 * drive urgent decisions, and a colour-only pill is unreadable to the ~8% of
 * men with colour-vision deficiency — and invisible in a printed job sheet.
 */

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Open",
  assigned: "Assigned",
  in_progress: "In progress",
  waiting_customer: "Waiting on customer",
  resolved: "Resolved",
  closed: "Closed",
  cancelled: "Cancelled",
};

export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

/** Inline style so the domain tokens in globals.css stay the one source. */
export function priorityStyle(priority: TicketPriority): { backgroundColor: string } {
  return { backgroundColor: `hsl(var(--priority-${priority}))` };
}

export function statusTone(status: TicketStatus): "muted" | "success" | "warning" | "danger" {
  switch (status) {
    case "resolved":
    case "closed":
      return "success";
    case "waiting_customer":
      return "warning";
    case "cancelled":
      return "danger";
    default:
      return "muted";
  }
}

/** Human phrasing for timeline entries. */
export const ACTION_LABELS: Record<string, string> = {
  created: "raised the ticket",
  assign: "assigned the ticket",
  self_assign: "picked up the ticket",
  accept: "accepted the ticket",
  wait_customer: "paused, waiting on the customer",
  resume: "resumed work",
  resolve: "marked it resolved",
  approve: "approved the resolution",
  reject: "rejected the resolution",
  close: "closed the ticket",
  auto_closed: "auto-closed (no customer response in 48h)",
  cancel: "cancelled the ticket",
  reopen: "reopened the ticket",
  priority_changed: "changed the priority",
  site_transferred: "moved the ticket to another site",
  merged: "merged this ticket into another",
  merged_in: "merged other tickets in",
  tags_changed: "updated the tags",
  note_added: "added a note",
  attachment_added: "attached a file",
  deleted: "removed the ticket",
};

/** Button text for the actions the server says are available. */
export const ACTION_BUTTONS: Record<string, string> = {
  assign: "Assign",
  self_assign: "Assign to me",
  accept: "Accept",
  wait_customer: "Wait on customer",
  resume: "Resume",
  resolve: "Resolve",
  approve: "Approve",
  reject: "Reject",
  close: "Close",
  cancel: "Cancel ticket",
  reopen: "Reopen",
};

/** Actions the server rejects without a reason — the UI must collect one. */
export const ACTIONS_NEEDING_REASON = new Set([
  "wait_customer",
  "resolve",
  "reject",
  "close",
  "cancel",
  "reopen",
]);
